import type {
    PortfolioBacktestTaxConfiguration,
    PortfolioBacktestTaxComparison as Comparison,
    PostTradeBalances
} from '@einhaus/project314-types';
import { getDateFromUnix } from '@einhaus/meteoric-helpers';
import PerformanceChart from '@/components/Chart/PerformanceChart';

const money = (value: number | null) => (value === null ? '—' : value.toLocaleString('en-US', { style: 'currency', currency: 'USD' }));
const percent = (value: number | null) => (value === null ? '—' : `${value.toFixed(2)}%`);
const decimal = (value: number | null) => (value === null ? '—' : value.toFixed(2));

export default function PortfolioBacktestTaxComparison({
    configuration,
    comparison,
    baselineBalances = []
}: {
    configuration: PortfolioBacktestTaxConfiguration;
    comparison: Comparison | null | undefined;
    baselineBalances?: PostTradeBalances[];
}) {
    const before = comparison?.statistics.find((row) => row.basis === 'beforeTax' && row.period === 'overall');
    const after = comparison?.statistics.find((row) => row.basis === 'afterTax' && row.period === 'overall');
    if (!comparison || !before || !after) return <div className="p-4">Tax comparison will be available when this backtest completes.</div>;
    const rows = [
        ['Ending portfolio value', money(before.endingEquity), money(after.endingEquity)],
        ['Total return', percent(before.returnPercent), percent(after.returnPercent)],
        ['CAGR', percent(before.cagr), percent(after.cagr)],
        ['ARORC', percent(before.returnOnRiskCapital), percent(after.returnOnRiskCapital)],
        ['Maximum drawdown', percent(before.maxDrawdown), percent(after.maxDrawdown)],
        ['Daily maximum drawdown', percent(before.dailyMaxDrawdown), percent(after.dailyMaxDrawdown)],
        ['Sharpe', decimal(before.sharpeRatio), decimal(after.sharpeRatio)],
        ['Accepted trades', String(before.numTrades), String(after.numTrades)],
        ['Trading profit before tax expense', money(before.tradingProfit), money(after.tradingProfit)],
        ['Trade win rate', percent(before.winRate), percent(after.winRate)],
        ['Average trade return before tax expense', percent(before.avgTradeProfit), percent(after.avgTradeProfit)]
    ];
    const netBalances = comparison.balances.map((point) => ({ timestamp: point.timestamp, balance: point.afterTaxEquity }));
    return (
        <div className="p-4 mt-3">
            <h4 className="mb-2">Before and after taxes</h4>
            <p className="text-sm text-zinc-300 mb-3">
                Federal {configuration.federalRatePercent}% + state {configuration.stateRatePercent}%. The after-tax run funds taxes from
                the portfolio and resizes later trades. Its value includes unpaid tax liabilities.
            </p>
            <div className="overflow-x-auto">
                <table className="w-full text-sm" aria-label="Before and after tax statistics">
                    <thead>
                        <tr>
                            <th className="p-2 text-left">Statistic</th>
                            <th className="p-2 text-right">Before taxes</th>
                            <th className="p-2 text-right">After taxes</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map(([label, first, second]) => (
                            <tr className="border-t border-zinc-700" key={label}>
                                <th scope="row" className="p-2 text-left font-normal">
                                    {label}
                                </th>
                                <td className="p-2 text-right">{first}</td>
                                <td className="p-2 text-right">{second}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            <p className="my-3 text-sm">
                Tax-funded account balance: {money(comparison.endingAccountEquity)} · Taxes paid: {money(comparison.taxesPaid)} · Taxes
                still owed: {money(comparison.unpaidTaxes)}
            </p>
            {comparison.statistics.length > 2 && (
                <div className="overflow-x-auto mb-4">
                    <table className="w-full text-sm" aria-label="Tax comparison by research window">
                        <thead>
                            <tr>
                                {['Window', 'Scenario', 'Return', 'CAGR', 'Maximum drawdown', 'Sharpe'].map((label) => (
                                    <th className="p-2 text-right" key={label}>
                                        {label}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {comparison.statistics
                                .filter((row) => row.period !== 'overall')
                                .map((row) => (
                                    <tr className="border-t border-zinc-700" key={`${row.basis}:${row.period}`}>
                                        <th scope="row" className="p-2 text-right">
                                            {row.period === 'training' ? 'Training' : 'Walk-forward'}
                                        </th>
                                        <td className="p-2 text-right">{row.basis === 'beforeTax' ? 'Before taxes' : 'After taxes'}</td>
                                        <td className="p-2 text-right">{percent(row.returnPercent)}</td>
                                        <td className="p-2 text-right">{percent(row.cagr)}</td>
                                        <td className="p-2 text-right">{percent(row.maxDrawdown)}</td>
                                        <td className="p-2 text-right">{decimal(row.sharpeRatio)}</td>
                                    </tr>
                                ))}
                        </tbody>
                    </table>
                </div>
            )}
            <p className="text-xs text-zinc-400 mb-2">
                Blue: untaxed baseline. Green: tax-funded portfolio after unpaid liabilities. Amounts include any deposits; total return and
                CAGR exclude deposits.
            </p>
            <PerformanceChart balances={baselineBalances} comparisonBalances={netBalances} height={300} />
            <div className="overflow-x-auto mt-4">
                <table className="w-full text-sm" aria-label="Annual portfolio taxes">
                    <thead>
                        <tr>
                            {[
                                'Tax year',
                                'Realized gains/losses',
                                'Other income',
                                'Loss carried in',
                                'Loss carried out',
                                'Federal tax',
                                'State tax',
                                'Paid',
                                'Payment date'
                            ].map((label) => (
                                <th className="p-2 text-right" key={label}>
                                    {label}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {comparison.years.map((year) => (
                            <tr className="border-t border-zinc-700" key={year.taxYear}>
                                <th scope="row" className="p-2 text-right">
                                    {year.taxYear}
                                    {year.isFinalized ? '' : ' (partial)'}
                                </th>
                                {[
                                    year.realizedGain,
                                    year.ordinaryIncome,
                                    year.lossCarryforwardIn,
                                    year.lossCarryforwardOut,
                                    year.federalTax,
                                    year.stateTax,
                                    year.federalTaxPaid + year.stateTaxPaid
                                ].map((value, index) => (
                                    <td className="p-2 text-right" key={index}>
                                        {money(value)}
                                    </td>
                                ))}
                                <td className="p-2 text-right whitespace-nowrap">{getDateFromUnix(year.paymentTimestamp, 'ymd')}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            <p className="mt-3 text-xs text-zinc-400">
                Flat-rate short-term estimate; no tax brackets, wash-sale adjustments, outside-account gains/losses, or tax benefits against
                outside income. Loss carryforwards have no assumed cash value. Payments occur on literal April 15; quarterly estimates and
                deadline extensions are excluded. Prior-year taxes are reserved from January 1. ARORC deducts the modeled tax expense; trade
                win rates and average trade returns describe trading results. Sharpe uses the same pre-tax Treasury benchmark in both
                scenarios. Existing statistics below describe the untaxed baseline.
            </p>
        </div>
    );
}
